import { useMutation } from '@tanstack/react-query';
import {
  LoginCredentials,
  LoginResponse,
  SSOApiResponse,
  loginRequiresMandatoryPasswordChange,
} from '@rfacil/shared/types';
import { apiSSO } from '@rfacil/shared/services';
import { useAuthStore } from '@rfacil/shared/services';
import { jwtDecode } from 'jwt-decode';
import { useUserContextStore } from '@rfacil/shared/services';
import { usePermissionsStore } from '@rfacil/shared/services';
import { User } from '@rfacil/shared/types';
import axios from 'axios';
import { getErrorMessage } from '../hooks/errorHelpers';
import { requestPasswordReset } from '../services/usuarioService';


/**
 * Función para realizar el login en la API
 */
async function loginAPI(credentials: LoginCredentials): Promise<LoginResponse> {
  const { data } = await apiSSO.post<SSOApiResponse<LoginResponse>>('/Login/login', credentials);
  return data.Data;
}

/**
 * Hook para realizar login usando TanStack Query
 */
export function useLogin() {
  return useMutation({
    mutationFn: loginAPI,
    onSuccess: (data, variables) => {
      try {
        // Decodificar el token JWT para obtener todos los datos disponibles
        const tokenDecoded = jwtDecode<{
          IdUsuario: number;
          IdCorporativo: number;
          IdEmpresa: number;
          Correo: string;
          [key: string]: unknown;
        }>(data.Token);

        const idUser = tokenDecoded.IdUsuario;
        const idCorporativo = tokenDecoded.IdCorporativo;
        const idEmpresa = tokenDecoded.IdEmpresa;
        const correo = tokenDecoded.Correo;

        const user: User = {
          id: idUser,
          name: data.NombreCompleto,
          email: correo, // Email desde las credenciales
          role: data.Rol,
          permissions: data.ClavesMenu,
          nombreCompleto: data.NombreCompleto,
          expiracion: data.Expiracion,
          idCorporativo: idCorporativo,
          idEmpresa: idEmpresa,
        };

        // Actualizar el store de autenticación usando la función login del store
        // Nota: Mantenemos la lógica en el store para no duplicar código
        // El hook solo maneja la llamada a la API, el store maneja el estado
        useAuthStore.setState({
          user: user,
          token: data.Token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
          idUser: idUser,
          expiracion: data.Expiracion,
          idCorporativo: idCorporativo,
          idEmpresa: idEmpresa,
          pendingPasswordChange:
            loginRequiresMandatoryPasswordChange(data),
        });

        // Establecer IdCorporativo e IdEmpresa en el store de contexto si están disponibles
        if (tokenDecoded.IdCorporativo && tokenDecoded.IdCorporativo > 0) {
          useUserContextStore.getState().setCorporativo(tokenDecoded.IdCorporativo);

        } else {
          console.warn('⚠️ IdCorporativo no encontrado en el token JWT');
        }

        if (tokenDecoded.IdEmpresa && tokenDecoded.IdEmpresa > 0) {
          useUserContextStore.getState().setEmpresa(tokenDecoded.IdEmpresa);

        } else {
          console.warn('⚠️ IdEmpresa no encontrada en el token JWT');
        }

        // Cargar permisos de secciones y actividades después del login
        usePermissionsStore.getState().fetchPermissions();

        window.dispatchEvent(
          new CustomEvent('userLoggedIn', {
            detail: {
              User: user,
              Token: data.Token,
              Permissions: data.ClavesMenu,
            },
          })
        );
      } catch (decodeError) {
        // Si hay un error al decodificar el token, tratarlo como error de autenticación
        console.error('Error al decodificar el token JWT:', decodeError);
        useAuthStore.setState({
          isLoading: false,
          error: 'Error al procesar la respuesta del servidor. Token inválido.',
        });
      }
    },
    onError: (error) => {
      // Si el error ya tiene un errorType personalizado (de loginAPI), usarlo directamente
      const errorWithType = error as Error & { errorType?: 'user' | 'password' | 'other' };
      if (errorWithType.errorType) {
        const errorMessage = errorWithType.message || 'Error al iniciar sesión';
        useAuthStore.setState({
          isLoading: false,
          error: errorMessage,
        });
        throw errorWithType;
      }

      // Mensaje base extraído del backend (descripcion, message, Message, error)
      const serverMessage = getErrorMessage(error);
      let errorMessage = serverMessage || 'Error al iniciar sesión';
      let errorType: 'user' | 'password' | 'other' = 'other';

      if (axios.isAxiosError(error) || (error as { isAxiosError?: boolean }).isAxiosError) {
        const axiosError = error as { response?: { status: number }; request?: unknown; message?: string };
        if (axiosError.response) {
          const status = axiosError.response.status;
          const normalizedMessage = serverMessage.toLowerCase();

          // Verificar si el error es relacionado con Base64/contraseña corrupta
          if (
            status === 500 &&
            (normalizedMessage.includes('base-64') ||
              normalizedMessage.includes('base64') ||
              (normalizedMessage.includes('contraseña') && normalizedMessage.includes('corrupta')))
          ) {
            errorMessage =
              'Error en el servidor: La contraseña almacenada para este usuario está corrupta o en un formato inválido. Contacta al administrador del sistema.';
            errorType = 'password';
          } else if (status === 401) {
            if (
              normalizedMessage.includes('usuario no existe') ||
              normalizedMessage.includes('usuario no encontrado') ||
              normalizedMessage.includes('user not found') ||
              normalizedMessage.includes('usuario inválido') ||
              normalizedMessage.includes('invalid user') ||
              normalizedMessage.includes('usuario incorrecto')
            ) {
              errorMessage = 'El usuario no existe. Verifica que el usuario sea correcto.';
              errorType = 'user';
            } else if (
              normalizedMessage.includes('contraseña incorrecta') ||
              normalizedMessage.includes('password incorrect') ||
              normalizedMessage.includes('contraseña inválida') ||
              normalizedMessage.includes('invalid password') ||
              normalizedMessage.includes('wrong password')
            ) {
              errorMessage = 'La contraseña es incorrecta. Verifica tu contraseña.';
              errorType = 'password';
            } else {
              errorMessage = serverMessage || 'Credenciales inválidas. Verifica tu usuario y contraseña.';
              errorType = 'other';
            }
          } else if (status === 404) {
            errorMessage = 'El usuario no existe. Verifica que el usuario sea correcto.';
            errorType = 'user';
          } else if (status === 500) {
            errorMessage =
              serverMessage || 'Error interno del servidor. Por favor, intenta nuevamente o contacta al soporte.';
            errorType = 'other';
          } else {
            errorMessage = serverMessage || `Error del servidor (${status})`;
            errorType = 'other';
          }
        } else if (axiosError.request) {
          const errorMsg = axiosError.message || (error instanceof Error ? error.message : '');
          if (errorMsg.includes('timeout')) {
            errorMessage =
              'El servidor no responde. Verifica que el backend esté ejecutándose en http://localhost:7260';
          } else if (errorMsg.includes('Network Error')) {
            errorMessage =
              'Error de conexión. Verifica que el backend esté ejecutándose y accesible.';
          } else if (errorMsg.includes('ECONNREFUSED')) {
            errorMessage =
              'No se puede conectar al backend. Verifica que esté ejecutándose en el puerto 7260.';
          } else {
            errorMessage = serverMessage || 'Error de conexión con el servidor.';
          }
          errorType = 'other';
        } else {
          errorMessage = serverMessage || 'Error al iniciar sesión';
          errorType = 'other';
        }
      } else if (error instanceof Error) {
        errorMessage = serverMessage || error.message;
        errorType = 'other';
      }

      useAuthStore.setState({
        isLoading: false,
        error: errorMessage,
      });

      const customError = new Error(errorMessage) as Error & { errorType?: 'user' | 'password' | 'other' };
      customError.errorType = errorType;
      throw customError;
    },
    onMutate: () => {
      // Establecer loading al iniciar la mutación
      useAuthStore.setState({ isLoading: true, error: null });
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (correo: string) => requestPasswordReset(correo),
  });
}

